import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import AutumnalDecorations from './AutumnalDecorations';
import PolaroidPhoto from './PolaroidPhoto';
import type { Photo } from '../types';

export default function GalleryView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    photos, 
    setPhotos, 
    setSelectedPhotoId, 
    userRole, 
    accessCode,
    setIsUploadModalOpen 
  } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const showAdminPanel = searchParams.get('admin') === 'true' && userRole === 'admin';

  // Fetch photos
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await fetch('/api/photos?limit=500');
        const data = await response.json();
        if (data.photos) {
          setPhotos(data.photos as Photo[]);
        }
      } catch (error) {
        console.error('Failed to fetch photos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotos();
  }, [setPhotos]);

  const handleDownloadAll = async () => {
    if (!accessCode) {
      alert('Admin access required');
      return;
    }

    setIsDownloading(true);
    setDownloadProgress('Preparing download...');

    try {
      const response = await fetch('/api/admin/download-all', {
        headers: {
          'Authorization': `Bearer ${accessCode}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      setDownloadProgress('Downloading...');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wedding-photos-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadProgress('Download complete!');
      setTimeout(() => {
        setDownloadProgress('');
      }, 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadProgress('Download failed. Please try again.');
      setTimeout(() => {
        setDownloadProgress('');
      }, 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo? This cannot be undone.')) {
      return;
    }

    setDeletingId(photoId);

    try {
      const response = await fetch(`/api/admin/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessCode}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Remove from local state
        setPhotos(prev => prev.filter(p => p.id !== photoId));
      } else {
        alert(data.error || 'Failed to delete photo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-autumn-cream relative">
      <AutumnalDecorations intensity="light" />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-autumn-cream/95 backdrop-blur-sm border-b border-autumn-gold/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-autumn-light-brown hover:text-autumn-brown transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-display font-semibold text-autumn-brown">
                  Photo Gallery
                </h1>
                <p className="text-sm font-body text-autumn-light-brown">
                  {photos.length} {photos.length === 1 ? 'memory' : 'memories'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="btn-autumn text-sm"
            >
              <span className="hidden sm:inline">Share a Photo</span>
              <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Admin Panel */}
      {showAdminPanel && (
        <div className="bg-autumn-orange/5 border-b border-autumn-orange/20">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-lg text-autumn-brown flex items-center gap-2">
                  <svg className="w-5 h-5 text-autumn-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Admin Panel
                </h2>
                <p className="font-body text-sm text-autumn-light-brown">
                  {photos.length} photos • {Math.round(photos.reduce((acc, p) => acc + p.fileSize, 0) / (1024 * 1024))}MB total
                </p>
              </div>

              <button
                onClick={handleDownloadAll}
                disabled={isDownloading || photos.length === 0}
                className="btn-autumn flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {downloadProgress}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 relative z-10">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-autumn-gold/20" />
                <div className="absolute inset-0 rounded-full border-4 border-autumn-gold border-t-transparent animate-spin" />
              </div>
              <p className="font-body text-autumn-light-brown">Loading memories...</p>
            </div>
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-autumn-gold/10 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-autumn-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-display text-autumn-brown mb-2">
              No Photos Yet
            </h2>
            <p className="font-body text-autumn-light-brown mb-6">
              Be the first to share a memory!
            </p>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="btn-autumn"
            >
              Share a Photo
            </button>
          </div>
        ) : (
          <div className="photo-grid">
            {photos.map((photo) => (
              <div key={photo.id} className="relative transform hover:scale-[1.02] transition-transform group">
                <PolaroidPhoto
                  photo={photo}
                  onClick={() => setSelectedPhotoId(photo.id)}
                  showDetails={true}
                  className="w-full"
                  style={{ transform: 'none' }}
                />
                {/* Delete button for admin */}
                {showAdminPanel && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photo.id);
                    }}
                    disabled={deletingId === photo.id}
                    className="absolute top-2 right-2 w-8 h-8 bg-autumn-red text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-700 disabled:opacity-50 z-10"
                    title="Delete photo"
                  >
                    {deletingId === photo.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Back to top button */}
      {photos.length > 10 && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-20 w-12 h-12 bg-autumn-orange text-white rounded-full shadow-lg flex items-center justify-center hover:bg-autumn-red transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
}

